/**
@format
@flow
*/
import React, { useEffect, useCallback, useReducer } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { Heading } from '@ziynx/components/typography';
import {
  Table, TableHead, TableBody, TableHeader, Cell, TableRow,
} from '@ziynx/components/tables';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import {
  withAppManager, LoadingSpinner, useNotification,
  getTotalFilters, findField,
} from '@ziynx/components/utilities';
import { InView } from 'react-intersection-observer';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import Colors from '@ziynx/theme/colors';
import { InputSearch } from '@ziynx/components/inputs';
import { ButtonPillGroup, Button } from '@ziynx/components/buttons';
import { Popover } from '@ziynx/components/popovers';
import Link from '@ziynx/components/links';
import Icon from '@ziynx/components/icons';
import ActionTypes from '@ziynx/action-types';
import type { Viewports } from '@ziynx/flow-types/globals';
import * as KrispyTokens from '@krispy-tokens/blue';

type DispatchAction = {
  type: string,
} => void;

type Props = $ReadOnly<{|
  /**
   * Viewport
   */
  viewport: Viewports,
  /**
   * Navigation stack
   */
  // history: {
  //   push: (pathName: string, params?: { from: string }) => void,
  //   goBack: () => void,
  //   location: {
  //     state: {
  //       from: string,
  //     }
  //   },
  // },

|}>;

const StyledDiv = styled.div`
  display: flex;
  justify-content: ${({ justifyContent }) => (justifyContent ?? 'space-between')};
  padding: 40px 40px 20px;
  @media screen and (min-width: 320px) and (max-width: 767px) {
    flex-direction: column;
    padding: 24px  16px;
  }
`;

const ContentWrapper = styled.div`
  overflow: scroll;
  width: 100%; 
  height: 100%;
  ${StyledDiv} + ${StyledDiv}  {
    padding-top: 0px;
  }
`;

const PillWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const HeaderLink = styled(Link)`
  display: block;
  margin-left: 20px;
`;

const FilterRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  @media screen and (min-width: 320px) and (max-width: 767px) {
    padding-top: 16px;
  }
`;

const InputContainer = styled.div`
  @media screen and (min-width: 320px) and (max-width: 767px) {
    padding-top: 24px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  td.hideOverflow {
    white-space: pre-wrap;
    max-width: 194px;
    > div {
      overflow: hidden;
    }
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100px;
`;

const StyledLink = styled(Link)`
  text-align: center;
  width: 16px;
  height: 16px;
`;

const PopoverContainer = styled.div`
  position: absolute;
  left: 50%;
`;

const TABLE_HEADERS = [
  { name: 'Name', value: 'fullName' },
  { name: 'Talent Id', value: 'profile' },
  { name: 'Discoverable', value: 'discoverable' },
  { name: 'Rating', value: 'rating' },
  { name: 'Job Status', value: 'jobStatus' },
  { name: 'Job Type', value: 'jobType' },
  { name: 'Note', value: 'talentNote' },
  { name: '', value: 'actions' },
];

// eslint-disable-next-line react/no-danger
const _decodeContent = (htmlString: string) => (<span dangerouslySetInnerHTML={{ __html: htmlString }} style={{ overflowWrap: 'anywhere' }} />);

const _renderCells = ({
  item, history, notificationDispatch,
}) => TABLE_HEADERS.map((category, i) => {
  let cellValue = 'Unknown';
  const overflowHidden = category?.value === 'talentNote';
  switch (category?.value) {
  case 'profile':
    cellValue = item?.profile;
    break;
  case 'fullName':
    cellValue = item?.fullName;
    break;
  case 'discoverable':
    cellValue = item?.discoverable ? 'Yes' : 'No';
    break;
  case 'rating':
    cellValue = item?.rating?.name || 'Unknown';
    break;
  case 'jobStatus':
    cellValue = item?.jobStatus?.name || 'Unknown';
    break;
  case 'jobType':
    cellValue = item?.jobType?.name || 'Unknown';
    break;
  case 'talentNote':
    cellValue = _decodeContent(item?.talentNotes || '-');
    break;
  case 'actions':
    cellValue = 'actions';
    cellValue = (
      <PopoverContainer>
        <Popover
          position="bottomLeft"
          links={[
            {
              children: 'Edit talent',
              large: true,
              onClick: () => {
                history.push(`/admin/talent/${item?._id}`, { from: 'admin/talent' });
              },
            },
            {
              children: 'Go to profile',
              large: true,
              onClick: () => {
                history.push(`/discover/talent/${item?.profile}`, { from: 'admin/talent' });
              },
            },
            {
              children: 'Copy public profile',
              large: true,
              onClick: () => {
                const profileUrl = `${window?.location?.hostname}/discover/talent/${item?.profile}`;
                navigator?.clipboard?.writeText(profileUrl);
                notificationDispatch({
                  type: ActionTypes.notifications.add.START,
                  payload: {
                    variant: 'success',
                    content: 'Public profile URL has been copied to clipboard.',
                  },
                });
              },
            },
          ]}
          trigger={(
            <StyledLink variant="inline">
              <Icon
                veryTiny
                name="more"
                color={KrispyTokens.KrispyColorPaletteGray12}
              />
            </StyledLink>
          )}
        />
      </PopoverContainer>
    );
    break;
  default:
    break;
  }
  return (
    <Cell
      className={overflowHidden ? 'hideOverflow' : undefined}
      key={`${i}-${item[category?.value]}`}
    >
      {cellValue}
    </Cell>
  );
});

const _renderTableHeader = (headers) => headers.map((header, i) => (
  <TableHeader key={i + header?.name}>{header?.name}</TableHeader>
));

function _renderTableBody({
  data, history, notificationDispatch,
}) {
  return data?.map((item, index) => (
    <TableRow key={index + item}>
      {_renderCells({
        item, history, notificationDispatch,
      })}
    </TableRow>
  ));
}

/**
 * reducer state
 {
    query: {
      name?: string,
    }
 }
 */
function reducer(state: ReducerState, action: ActionType) {
  const { type } = action;

  switch (type) {
  case 'setQueryState':
    return {
      ...state,
      query: {
        ...action?.query || {},
      },
    };
  default:
    return state;
  }
}

const initialReducerState: ReducerState = {
  fetching: false,
  query: {},
};

const TalentList = (props: Props) => {
  // eslint-disable-next-line max-len
  const scorecardService = useSelector(({ adminTalentScorecards }) => (adminTalentScorecards || {}), shallowEqual);
  // eslint-disable-next-line max-len
  const filterService = useSelector(({ adminTalentFilters }) => adminTalentFilters?.appliedFilters ?? {}, shallowEqual);

  const dispatch: DispatchAction = useDispatch();

  const history: History = useHistory();

  const { viewport } = props;

  const [state, dispatchReducer] = useReducer(reducer, initialReducerState);
  const { notificationDispatch } = useNotification();

  useEffect(() => {
    dispatch({
      type: ActionTypes.admin.getAllScorecards.START,
      page: 0,
    });
  }, []);

  const _onChange = (evt) => {
    const target = evt?.target;
    const name = target?.value?.toString();
    const query = name?.length > 0 ? { name } : {};

    dispatchReducer({
      type: 'setQueryState',
      query,
    });

    dispatch({
      type: ActionTypes.admin.getAllScorecards.START,
      page: 0,
      body: query,
    });
  };

  useEffect(() => {
    dispatch({
      type: ActionTypes.admin.getAllScorecards.START,
      body: {
        ...filterService,
      },
    });
  }, [filterService]);

  const _onClear = () => {
    dispatchReducer({
      type: 'setQueryState',
      query: {
        name: undefined,
      },
    });

    dispatch({
      type: ActionTypes.admin.getAllScorecards.START,
      page: 0,
    });
  };

  const _debouncedChangeHandler = useCallback(debounce(_onChange, 300), []);

  const _onPaginate = (inView): void => {
    if (scorecardService?.isLastPage || scorecardService?.fetching || !inView) return;
    const body = state?.query || {};
    dispatch({
      type: ActionTypes.admin.getAllScorecards.START,
      page: scorecardService?.page + 1,
      body,
    });
  };

  const _renderFilterPills = () => {
    const FILTER_TOTAL = getTotalFilters(Object.values(filterService ?? {})).length;
    const FILTER_RESULTS = getTotalFilters(
      Object.values(filterService ?? {}), FILTER_TOTAL,
    )
      .map((filter: any) => ({
        label: {
          text: filter?.name ? filter?.name : filter,
          color: Colors.GrayVeryDark,
        },
        iconRight: {
          name: 'close',
          color: Colors.GrayVeryDark,
        },
        hoverConfig: {
          iconRight: Colors.PrimaryBlue,
        },
        width: 'auto',
        onClick: () => {
          dispatch({
            type: ActionTypes.admin.filters.remove,
            removeFromApplied: true,
            filters: {
              fieldId: findField(filterService, filter?.name ? filter?.name : filter),
              content: filter?.name ? filter?.name : filter,
            },
          });
        },
      }));

    return (
      <PillWrapper>
        {!FILTER_TOTAL ? (
          <Button
            variant="pill"
            label={{
              text: 'No filters applied',
              color: Colors.GrayDark,
            }}
            iconLeft={{
              name: 'filter',
              color: Colors.Gray,
            }}
            backgroundColor={Colors.GrayVeryLight}
            onClick={() => { history.push('/admin/talent/filter'); }}
          />
        ) : (
          <ButtonPillGroup
            pills={FILTER_RESULTS}
            stacked={false}
          />
        )}
      </PillWrapper>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          Talent List | ZIYNX Admin
        </title>
      </Helmet>
      <ContentWrapper>
        <StyledDiv>
          <HeadingContainer>
            <Heading large>Talent list</Heading>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <HeaderLink
              color={Colors.PrimaryBlue}
              onClick={() => { history.push('/admin/talent/filter'); }}
              large
              variant="text"
            >
              Filter
            </HeaderLink>
          </HeadingContainer>
          <InputContainer>
            <InputSearch
              label="Search talent"
              disabled
              onChange={_debouncedChangeHandler}
              onClear={_onClear}
              width={viewport === 'mobile' ? '100%' : '500px'}
            />
          </InputContainer>
        </StyledDiv>
        <StyledDiv justifyContent="flex-start">
          <FilterRowWrapper>
            {_renderFilterPills()}
          </FilterRowWrapper>
        </StyledDiv>
        <TableContainer>
          <Table>
            <TableHead>{_renderTableHeader(TABLE_HEADERS)}</TableHead>
            <TableBody>
              {_renderTableBody({
                data: scorecardService?.results || [],
                history,
                notificationDispatch,
              })}
            </TableBody>
          </Table>
          <InView as="div" onChange={_onPaginate}>
            <SpinnerWrapper>
              {!scorecardService?.isLastPage && scorecardService?.fetching && <LoadingSpinner />}
            </SpinnerWrapper>
          </InView>
        </TableContainer>
      </ContentWrapper>
    </>
  );
};

export default (withAppManager(TalentList): React$ComponentType<ManagedProps<Props>>);
